// ItemGrid — the 2-to-N column grid used for both the drilled-tile
// view and the search-hits view. Renders memoized KitchenCard rows
// directly. Per-item framer-motion entrance/popLayout was dropped:
// on a 30-row pantry, every state change in the parent (search
// focus, theme tick, sticky-shadow flip) was running framer's
// layout pipeline on every card. The CSS keyframe entrance on the
// container covers the visual without per-card listeners.

import { useCallback, useMemo } from "react";
import { KitchenCard } from "./KitchenCard";
import { LOCATIONS } from "./helpers";
import { findIngredient, HUBS } from "../../data/ingredients";
import { groupCardsForKitchen } from "../../lib/kitchenGrouping";

// Hub lookup is separate from findIngredient. Hubs ("cheese_hub",
// "chicken_hub", etc.) live in the HUBS array; findIngredient reads
// from INGREDIENTS only. Without this map the breadcrumb's hub
// segment silently dropped — "Fridge · Dairy & Eggs · Parmesan"
// instead of the intended "Fridge · Dairy & Eggs · Cheese ·
// Parmesan."
const HUBS_BY_ID = new Map((HUBS || []).map((h) => [h.id, h]));

export function ItemGrid({
  items,
  onOpenItem,
  onOpenUnitPicker,
  onUpdateItem,
  // Hold-menu callbacks for the per-card 3D-flip action panel.
  // Each is forwarded to KitchenCard's onDelete / onRestock props.
  // Optional — when omitted, the corresponding back-face button
  // disables itself.
  onDeleteItem = null,
  onRestockItem = null,
  // Stack drill-in. Fired when the user taps the "×N" count badge
  // on a grouped card; KitchenScreen mounts StackDetailSheet on the
  // members. Optional — when null, the badge still renders but
  // becomes inert (good for Showcase / read-only contexts).
  onOpenStack = null,
  showTileContext = false,
  brandSuggestions = [],
  // Multi-select mode (bulk cleanup). Selection operates on the
  // RENDERED GROUP — tapping a "×3" stack selects all three physical
  // rows, which is what "clear out these tomatoes" means. Cards stop
  // opening; an overlay intercepts taps and renders the check ring.
  selectMode = false,
  selectedIds = null,      // Set<rowId>
  onToggleSelect = null,   // (memberRaws[]) => void
  // Long-press on a card (normal mode) enters select mode with that
  // card's group pre-selected. Receives memberRaws[] like toggle.
  onHoldSelect = null,
}) {
  // Stable per-grid callbacks so KitchenCard's React.memo isn't
  // defeated by inline arrows on every parent render. Each typing
  // tick / realtime tick used to recreate `onPick`/`onUpdate` arrows
  // for every card, breaking memo and re-rendering the entire list.
  const handlePick = useCallback((it) => {
    if (onOpenItem && it._raw) onOpenItem(it._raw);
    else if (onOpenUnitPicker) onOpenUnitPicker();
  }, [onOpenItem, onOpenUnitPicker]);
  const handleUpdate = useCallback((it, patch) => {
    if (onUpdateItem && it._raw) onUpdateItem(it._raw, patch);
  }, [onUpdateItem]);
  // Hold-menu handlers receive the unwrapped `_raw` row directly
  // from KitchenCard (it's already extracted there before the call).
  // Forward to the parent. useCallback so the ItemGrid → KitchenCard
  // memo barrier holds across parent rerenders.
  const handleDelete = useCallback((raw) => {
    if (onDeleteItem && raw) onDeleteItem(raw);
  }, [onDeleteItem]);
  const handleRestock = useCallback((raw) => {
    if (onRestockItem && raw) onRestockItem(raw);
  }, [onRestockItem]);
  // Memoize the tile-label lookup so the same string identity comes
  // back across renders for an unchanged item — KitchenCard's memo
  // doesn't see the prop "change" on every parent re-render.
  //
  // Returns the FULL placement breadcrumb so the user can audit where
  // each item is landing — location → tile → parent hub → canonical.
  // For "Belgioioso Parmesan (28 G)":
  //   Fridge · Dairy & Eggs · Cheese · Parmesan
  // Each segment resolves independently: items without a canonical
  // hub still show location + tile + canonical; items without a
  // canonical fall back to location + tile.
  const tileLabelFor = useCallback((item) => {
    if (!showTileContext) return null;
    const loc = LOCATIONS.find(l => l.id === item._location);
    const tile = loc?.tiles.find(t => t.id === item._tileId);
    const ing = item.canonicalId ? findIngredient(item.canonicalId) : null;
    const hub = ing?.parentId ? HUBS_BY_ID.get(ing.parentId) : null;
    const parts = [loc?.label, tile?.label, hub?.name, ing?.name].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [showTileContext]);
  // Collapse identical pantry rows into stacked groups — 3 cans of
  // Hunt's Diced Tomatoes 14oz render as ONE card with "×3 →"
  // instead of three identical cards stacked in the grid. The data
  // model stays 1-row-per-physical-package (each can keeps its own
  // expiry / partial state); this is render-time only. Stable
  // memoization on `items` so realtime ticks / theme flips don't
  // re-bucket needlessly.
  const groups = useMemo(() => groupCardsForKitchen(items), [items]);

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: 18,
      marginTop: 20,
    }}>
      {groups.map((g) => {
        const memberRaws = g.members
          .map((m) => m._raw || m)
          .filter((r) => r && r.id && !r.protected);
        const card = (
          <KitchenCard
            key={selectMode ? undefined : g.leader.id}
            item={g.leader}
            tileLabel={tileLabelFor(g.leader)}
            brandSuggestions={brandSuggestions}
            onPick={handlePick}
            onUpdate={onUpdateItem ? handleUpdate : null}
            onDelete={onDeleteItem ? handleDelete : null}
            onRestock={onRestockItem ? handleRestock : null}
            stackCount={g.members.length}
            onOpenStack={
              onOpenStack && g.members.length > 1
                ? () => onOpenStack(g.members)
                : null
            }
            onHoldSelect={
              onHoldSelect && memberRaws.length > 0
                ? () => onHoldSelect(memberRaws)
                : null
            }
          />
        );
        if (!selectMode) return card;
        // Select mode: full-cover overlay intercepts the tap (the
        // memoized KitchenCard stays untouched — no prop churn, no
        // flip/hold interference) and renders the selection state.
        const isSelected = memberRaws.length > 0
          && memberRaws.every((r) => selectedIds?.has(r.id));
        const selectable = memberRaws.length > 0;
        return (
          <div key={g.leader.id} style={{ position: "relative" }}>
            <div style={{
              opacity: isSelected ? 1 : 0.82,
              transition: "opacity 120ms ease",
            }}>
              {card}
            </div>
            <button
              type="button"
              aria-pressed={isSelected}
              aria-label={`${isSelected ? "Deselect" : "Select"} ${g.leader.name || "item"}`}
              onClick={() => { if (selectable) onToggleSelect?.(memberRaws); }}
              style={{
                position: "absolute", inset: 0,
                background: "transparent",
                border: isSelected ? `2px solid ${"#D4A637"}` : "2px solid transparent",
                borderRadius: 18,
                cursor: selectable ? "pointer" : "default",
                padding: 0,
                touchAction: "manipulation",
              }}
            />
            <div style={{
              position: "absolute", top: 8, right: 8,
              width: 24, height: 24, borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
              background: isSelected ? "#D4A637" : "rgba(24,18,10,0.55)",
              border: `1.5px solid ${isSelected ? "#D4A637" : "rgba(255,255,255,0.55)"}`,
              color: isSelected ? "#1A130A" : "transparent",
              fontFamily: "inherit", fontSize: 13, fontWeight: 800,
              transition: "background 120ms ease",
            }}>
              ✓
            </div>
          </div>
        );
      })}
    </div>
  );
}
